import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "@/components/ui/AlertDialog";
import { Button, ButtonShadcn } from "@/components/ui/Button";
import RHFTextArea from "@/components/form/RHFTextArea";
import { RotateCcw, X } from "lucide-react";

import { getStatusLabel } from "../constants/actions";
import { REJECT_NOTE_MIN_LENGTH, REVIEW_NOTE_MAX_LENGTH, undoReviewNoteDefaultValues, undoReviewNoteSchema } from "../validation.schema";

const formatWhen = (iso) => (iso ? new Date(iso).toLocaleString() : "");

const Row = ({ label, children }) => (
  <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
    <dt className="w-32 shrink-0 text-sm text-text-secondary">{label}</dt>
    <dd className="m-0 text-sm font-medium text-text-primary">{children}</dd>
  </div>
);

/**
 * A pending undo request, for whoever opens the marker on the status control.
 *
 * What the footer offers depends on who is looking, and the server decides that
 * (`can_review`, `can_cancel`): the approver gets Approve and Reject with a note,
 * the requester gets Cancel request, and everyone else gets the facts and a
 * Close. Nothing here is a second opinion on permissions, and the server
 * refuses what the page does not offer all the same.
 *
 * A rejection says why, since the requester reads it; an approval may leave the
 * note empty.
 */
const UndoReviewModal = ({ isOpen, onClose, request, unavailable = false, candidateName, onApprove, onReject, onCancelRequest, isLoading }) => {
  const methods = useForm({
    resolver: zodResolver(undoReviewNoteSchema),
    mode: "onSubmit",
    defaultValues: undoReviewNoteDefaultValues,
  });
  const { handleSubmit, setError } = methods;

  const dismiss = () => {
    if (isLoading) return;
    onClose();
  };

  const approve = handleSubmit(({ note }) => onApprove(note));

  const reject = handleSubmit(({ note }) => {
    if (note.length < REJECT_NOTE_MIN_LENGTH) {
      setError("note", { type: "min", message: `Note must be at least ${REJECT_NOTE_MIN_LENGTH} characters` });
      return;
    }
    onReject(note);
  });

  const busy = (
    <>
      <RotateCcw size={14} className="animate-spin" aria-hidden="true" />
    </>
  );

  return (
    <AlertDialog open={!!isOpen} onOpenChange={(open) => !open && dismiss()}>
      <AlertDialogContent className="!max-w-2xl" overlayClassName="supports-backdrop-filter:backdrop-blur-md" onOverlayClick={dismiss}>
        <ButtonShadcn
          variant="ghost"
          size="icon"
          onClick={dismiss}
          disabled={isLoading}
          aria-label="Close"
          className="absolute right-4 top-4 flex h-7 w-7 items-center justify-center rounded-full bg-background-tertiary text-text-secondary transition-colors hover:bg-background-hover hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          <X size={16} aria-hidden="true" />
        </ButtonShadcn>

        <AlertDialogHeader>
          <AlertDialogTitle className="border-b border-border-primary pb-1 pr-8 text-lg">Candidate undo request</AlertDialogTitle>
          <AlertDialogDescription className="pr-8">
            {request?.can_review
              ? "Approving moves the candidate back to the stage below. Rejecting leaves them where they are."
              : "Status changes are locked until this request is decided."}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {request ? (
          <dl className="m-0 flex flex-col gap-2">
            <Row label="Candidate">{candidateName || request.candidate_name}</Row>
            <Row label="Current status">{getStatusLabel(request.original_status)}</Row>
            <Row label="Would return to">{getStatusLabel(request.target_status)}</Row>
            <Row label="Requested by">
              <span>{request.requested_by?.name}</span>
            </Row>
            <Row label="Requested on">{formatWhen(request.requested_at)}</Row>
            <Row label="Reason">
              <span className="whitespace-pre-wrap font-normal">{request.reason}</span>
            </Row>
            {request.no_eligible_approver && (
              <p className="m-0 rounded-lg border border-border-primary bg-background-secondary px-3 py-2 text-sm text-text-primary">
                No eligible approver is currently available. It will wait until an administrator other than the requester can review it.
              </p>
            )}
          </dl>
        ) : (
          <p className="m-0 text-sm text-text-secondary">{unavailable ? "You don't have access to this request's details." : "Loading the request…"}</p>
        )}

        {request?.can_review && (
          <FormProvider {...methods}>
            <form onSubmit={(e) => e.preventDefault()}>
              <RHFTextArea
                name="note"
                id="undo-review-note"
                label="Note for the requester"
                placeholder="Optional when approving. Required when rejecting, so the requester knows why."
                rows={3}
                maxLength={REVIEW_NOTE_MAX_LENGTH}
                counter="near-limit"
                disabled={isLoading}
                className="mb-2"
                inputClass="resize-y min-h-20 max-h-40 overflow-y-auto"
              />
              <AlertDialogFooter>
                <AlertDialogCancel onClick={onClose} disabled={isLoading}>
                  Close
                </AlertDialogCancel>
                <Button
                  type="button"
                  onClick={reject}
                  disabled={isLoading}
                  className="inline-flex h-8 items-center justify-center gap-2 rounded-md border border-border-primary bg-background-primary px-4 text-sm font-medium text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Reject
                </Button>
                <Button
                  type="button"
                  onClick={approve}
                  disabled={isLoading}
                  className="inline-flex h-8 items-center justify-center gap-2 rounded-md bg-button-secondary px-4 text-sm font-medium text-text-base disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isLoading && busy}
                  Approve
                </Button>
              </AlertDialogFooter>
            </form>
          </FormProvider>
        )}

        {request && !request.can_review && (
          <AlertDialogFooter>
            <AlertDialogCancel onClick={onClose} disabled={isLoading}>
              Close
            </AlertDialogCancel>
            {request.can_cancel && (
              <Button
                type="button"
                onClick={onCancelRequest}
                disabled={isLoading}
                className="inline-flex h-8 items-center justify-center gap-2 rounded-md bg-button-secondary px-4 text-sm font-medium text-text-base disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading && busy}
                Cancel request
              </Button>
            )}
          </AlertDialogFooter>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default UndoReviewModal;
